import { getLogger } from '@logtape/logtape';
import type { CommentFlag } from './commentFlag';

const log = getLogger(['platform', 'comment-flags']);
const STORAGE_KEY = 'platform:commentFlags';

// Best-effort localStorage persistence: never throws on read/write.
function load(): CommentFlag[] {
	if (typeof localStorage === 'undefined') return [];
	const raw = localStorage.getItem(STORAGE_KEY);
	if (!raw) return [];
	try {
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? (parsed as CommentFlag[]) : [];
	} catch {
		log.warn('Discarding unparseable comment-flag store');
		return [];
	}
}

function save(flags: CommentFlag[]) {
	if (typeof localStorage === 'undefined') return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
	} catch (error) {
		log.warn('Could not persist comment flags: {error}', { error });
	}
}

/** Exported for tests (fresh store per localStorage stub). App code uses the singleton below. */
export function createCommentFlagStore() {
	let flags = $state<CommentFlag[]>(load());
	let commentsVisible = $state(true);

	return {
		/** Reactive in-memory list; the source of truth for rendering. */
		get flags() {
			return flags;
		},
		/** Whether comment flags are shown on the map (toggled from the filter panel). */
		get commentsVisible() {
			return commentsVisible;
		},
		setCommentsVisible(visible: boolean) {
			commentsVisible = visible;
		},
		add(flag: CommentFlag) {
			flags = [...flags, flag];
			save(flags);
		},
		remove(id: string) {
			flags = flags.filter((f) => f.id !== id);
			save(flags);
		},
		/** Delete every flag belonging to one app (other apps' flags are untouched). */
		clearApp(app: string) {
			flags = flags.filter((f) => f.app !== app);
			save(flags);
		}
	};
}

export const commentFlagStore = createCommentFlagStore();
